/* IPO Journey Planner — six expandable stages from readiness to listing.
 * Timelines auto-scale to a user-set Target Filing Date and each step links
 * to the relevant module (Advisor Marketplace, DRHP Workspace, Document Vault,
 * Valuation Studio, Readiness Score, Funding Desk).
 *
 * Rendered inside the IPO Navigator (`.ipo-nav-root`). Receives `setScreen`
 * to switch IPO Navigator screens; uses react-router for external routes. */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./JourneyPlanner.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// go: { type: "screen"|"route"|"toast", to/msg }
const STAGES = [
  {
    n: 1, name: "IPO Readiness", weight: 2.0,
    sub: "Decide to go public, test eligibility, get the board and shareholders on board",
    steps: [
      { h: "Run an IPO readiness diagnostic", d: "A mock-IPO assessment of financials, governance and disclosures — mirrors how SEBI and your banker will review you. Doing this 6 months early prevents the biggest delays.", tags: [{ k: "mod", label: "IPO Navigator", icon: "ti-dashboard" }, { k: "who", label: "Founder + CFO", icon: "ti-user" }], go: { type: "screen", to: "readiness" } },
      { h: "Check SEBI eligibility (Main Board vs SME)", d: "Confirm post-issue capital, 3-year track record, net worth, net tangible assets and operating-profit thresholds for your chosen platform.", tags: [{ k: "mod", label: "Eligibility check", icon: "ti-checklist" }], go: { type: "screen", to: "readiness" } },
      { h: "Board & shareholder approval", d: "Pass the board resolution and special resolution (Section 62) authorising the IPO. Allow ~2 months for the meetings and notice periods.", tags: [{ k: "who", label: "Board + CS", icon: "ti-users" }, { k: "doc", label: "Resolutions", icon: "ti-file" }] },
      { h: "Convert to public limited & clean up the cap table", d: "Ensure the entity is a public limited company, regularise any related-party items, and resolve outstanding convertibles before filing.", tags: [{ k: "who", label: "CS + Legal", icon: "ti-file-certificate" }] },
    ],
  },
  {
    n: 2, name: "Team Assembly", weight: 0.85,
    sub: "Appoint the merchant banker and the full cast of intermediaries",
    steps: [
      { h: "Appoint the Book Running Lead Manager (BRLM)", d: "The SEBI-registered merchant banker who leads the entire issue — picks the exchange, coordinates intermediaries, drafts the DRHP and runs the book.", tags: [{ k: "mod", label: "Advisor Marketplace", icon: "ti-briefcase" }, { k: "who", label: "BRLM", icon: "ti-building-bank" }], go: { type: "screen", to: "advisors" } },
      { h: "Engage statutory auditor, peer-review auditor & legal counsel", d: "Each becomes an owner of their DRHP chapters. The peer-review auditor is SEBI-mandated; legal handles due diligence and litigation disclosure.", tags: [{ k: "mod", label: "Advisor Marketplace", icon: "ti-briefcase" }, { k: "who", label: "CA + Legal", icon: "ti-calculator" }], go: { type: "screen", to: "advisors" } },
      { h: "Appoint registrar (RTA), bankers & market maker", d: "The RTA handles allotment; the market maker (mandatory for SME) provides two-way quotes post-listing for the stipulated period.", tags: [{ k: "mod", label: "Advisor Marketplace", icon: "ti-briefcase" }, { k: "who", label: "RTA + Market maker", icon: "ti-id" }], go: { type: "screen", to: "advisors" } },
      { h: "Merchant banker due diligence", d: "The BRLM reviews financials, key contracts, statutory approvals and promoter credentials, then finalises the issue structure, size and pricing strategy.", tags: [{ k: "who", label: "BRLM", icon: "ti-building-bank" }] },
    ],
  },
  {
    n: 3, name: "DRHP Drafting", weight: 4.0,
    sub: "Build, populate and finalise the Draft Red Herring Prospectus",
    steps: [
      { h: "Capture master data once", d: "Enter company, promoters, directors, advisors and issue details a single time — they auto-populate the ~2,970 placeholders across all 39 chapters.", tags: [{ k: "mod", label: "Master Data", icon: "ti-database" }], go: { type: "route", to: "/drhp-workspace?phase=DATA_COLLECTION" } },
      { h: "Co-draft the chapters with your advisors", d: "Each owner drafts their sections — risk factors (legal), restated financials (auditor), business (founder) — with tracked changes and comments.", tags: [{ k: "mod", label: "DRHP Workspace", icon: "ti-file-text" }, { k: "who", label: "All advisors", icon: "ti-users" }], go: { type: "route", to: "/drhp-workspace?phase=DRAFTING" } },
      { h: "Restate 3 years of financials", d: "The auditor restates accounts under SEBI ICDR and certifies them. This is the longest pole — start the IPO-focused audit early.", tags: [{ k: "who", label: "Statutory auditor", icon: "ti-calculator" }, { k: "doc", label: "Restated financials", icon: "ti-report-money" }] },
      { h: "Run consistency check & resolve mismatches", d: "Reconcile every repeated figure and name across chapters — mismatches are the most common SEBI observation. Fix all before filing.", tags: [{ k: "mod", label: "Linked Fields Map", icon: "ti-checkup-list" }], go: { type: "route", to: "/drhp-workspace?phase=LINKED_MAP" } },
      { h: "Collect supporting documents in the Vault", d: "Material contracts, approvals, KYC, lock-in undertakings — every document SEBI expects, tracked and versioned.", tags: [{ k: "mod", label: "Document Vault", icon: "ti-files" }], go: { type: "screen", to: "docs" } },
      { h: "Final sign-offs & board adoption of the DRHP", d: "Each professional attests their chapters; the board adopts the DRHP before it's filed.", tags: [{ k: "who", label: "All owners + Board", icon: "ti-signature" }] },
    ],
  },
  {
    n: 4, name: "Filing & SEBI / Exchange Review", weight: 1.5,
    sub: "File the DRHP, clear the site visit, answer observations, get approval",
    steps: [
      { h: "File the DRHP with the exchange (SME) / SEBI", d: "For SME, the BRLM files directly with BSE SME / NSE Emerge; SEBI's in-principle approval and the one-month public notice are waived for SME.", tags: [{ k: "who", label: "BRLM", icon: "ti-building-bank" }, { k: "doc", label: "DRHP", icon: "ti-file-text" }], go: { type: "route", to: "/drhp-workspace?phase=LINKED_MAP" } },
      { h: "Exchange verification & site visit", d: "An exchange official inspects your facility and verifies the documents — be ready to walk them through operations.", tags: [{ k: "who", label: "Company", icon: "ti-building-factory" }] },
      { h: "Listing Advisory Committee interview", d: "Promoters are called to present before the committee. Their recommendation drives the in-principle approval.", tags: [{ k: "who", label: "Promoters", icon: "ti-crown" }] },
      { h: "Respond to observations within the timeline", d: "SEBI / the exchange issue observations (typically within ~30 days). Address each in the prescribed time — clean disclosures shorten this cycle.", tags: [{ k: "mod", label: "DRHP Workspace", icon: "ti-file-text" }, { k: "who", label: "BRLM + advisors", icon: "ti-briefcase" }], go: { type: "route", to: "/drhp-workspace?phase=DRAFTING" } },
      { h: "Receive in-principle approval", d: "The exchange issues approval on the committee's recommendation — your green light to move to pricing and the RHP.", tags: [] },
    ],
  },
  {
    n: 5, name: "Pricing, RHP & Roadshow", weight: 0.85,
    sub: "Finalise price, file the RHP with RoC, market the issue, lock anchors",
    steps: [
      { h: "Set the price band & issue size", d: "Freely priced with the banker; the cap can't exceed 20% above the floor. The price band isn't in the DRHP — it's fixed now.", tags: [{ k: "mod", label: "Valuation Studio", icon: "ti-chart-line" }, { k: "who", label: "BRLM", icon: "ti-building-bank" }], go: { type: "screen", to: "valuation" } },
      { h: "File the RHP with the Registrar of Companies", d: "The Red Herring Prospectus adds the final price band and the open/close dates, then is filed with RoC.", tags: [{ k: "doc", label: "RHP", icon: "ti-file-text" }, { k: "who", label: "CS + BRLM", icon: "ti-file-certificate" }] },
      { h: "Build the IR deck & run the roadshow", d: "Present to anchors, QIBs and HNIs. Keep every claim reconciled to the RHP and inside SEBI's advertising code.", tags: [{ k: "mod", label: "IR Deck Builder", icon: "ti-presentation" }], go: { type: "toast", msg: "IR Deck Builder is coming soon — your roadshow deck workspace." } },
      { h: "Lock the anchor book (T-1 day)", d: "Anchors commit one day before the issue opens, with the two-tier 30/90-day lock-in. A strong anchor book de-risks subscription.", tags: [{ k: "mod", label: "Funding Desk", icon: "ti-anchor" }], go: { type: "route", to: "/funding" } },
    ],
  },
  {
    n: 6, name: "Subscription, Allotment & Listing", weight: 0.4,
    sub: "Open the issue, allot shares, list and ring the bell",
    steps: [
      { h: "Issue opens for public subscription", d: "The book is open ~3 days for retail, NII and QIB bids. Track demand across categories in real time.", tags: [{ k: "who", label: "Public + BRLM", icon: "ti-users" }] },
      { h: "Close issue & finalise basis of allotment", d: "The registrar and exchange determine allotment; refunds and credits to demat follow.", tags: [{ k: "who", label: "Registrar (RTA)", icon: "ti-id" }] },
      { h: "Listing & first trade (T+3)", d: "Shares list on BSE SME / NSE Emerge within 3 working days of issue close. The market maker begins two-way quotes.", tags: [{ k: "who", label: "Exchange", icon: "ti-businessplan" }] },
      { h: "Post-listing compliance begins", d: "Ongoing disclosures, market-making obligations, and promoter lock-in (half released after 1 year, half after 2). Consider post-IPO stabilisation capital.", tags: [{ k: "mod", label: "Funding Desk", icon: "ti-coins" }, { k: "who", label: "CS", icon: "ti-file-certificate" }], go: { type: "route", to: "/funding" } },
    ],
  },
];

const TOTAL_WEIGHT = STAGES.reduce((s, x) => s + x.weight, 0);
const PRESETS = [{ m: 12, label: "12 months" }, { m: 18, label: "18 months" }, { m: 24, label: "24 months" }];

const addMonths = (date, months) => {
  const d = new Date(date);
  const whole = Math.floor(months);
  const frac = months - whole;
  d.setMonth(d.getMonth() + whole);
  d.setDate(d.getDate() + Math.round(frac * 30.44));
  return d;
};
const monthsBetween = (a, b) => (b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
const fmtMon = (d) => d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
const fmtDur = (m) => {
  if (m < 1) return `~${Math.max(1, Math.round(m * 4.345))} weeks`;
  const r = Math.round(m * 10) / 10;
  return `~${Number.isInteger(r) ? r : r.toFixed(1)} months`;
};
const toInputDate = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

const JourneyPlanner = ({ setScreen }) => {
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const today = useMemo(() => new Date(), []);
  const defaultDate = useMemo(() => toInputDate(addMonths(today, 12)), [today]);
  const [targetDate, setTargetDate] = useState(defaultDate);
  const [open, setOpen] = useState({});

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/workspace`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (!active) return;
        setWs(d);
        if (d?.target_filing_date) setTargetDate(d.target_filing_date);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const stage = ws?.stage || 1;

  // Open the current stage by default once we know it.
  useEffect(() => { setOpen({ [stage]: true }); }, [stage]);

  const saveDate = (val) => {
    setTargetDate(val);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) return;
    fetch(`${API_URL}/api/workspace/target-filing-date`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ target_filing_date: val }),
    }).catch(() => {});
  };

  // Compute per-stage windows from today → targetDate, split by weight.
  const plan = useMemo(() => {
    const target = new Date(targetDate);
    const totalMonths = Math.max(3, monthsBetween(today, target));
    let cursor = 0;
    return STAGES.map((s) => {
      const dur = (s.weight / TOTAL_WEIGHT) * totalMonths;
      const start = addMonths(today, cursor);
      const end = addMonths(today, cursor + dur);
      cursor += dur;
      return { ...s, dur, start, end, durLabel: fmtDur(dur) };
    });
  }, [targetDate, today]);

  const totalMonths = Math.max(3, monthsBetween(today, new Date(targetDate)));
  // Months until the DRHP is filed = end of stage 3.
  const filingMonths = plan.slice(0, 3).reduce((acc, s) => acc + s.dur, 0);
  const progressPct = Math.round((((stage - 1) + 0.5) / STAGES.length) * 100);
  const currentName = STAGES[Math.min(stage, STAGES.length) - 1]?.name || STAGES[0].name;

  const companyName = (ws?.company?.legal_name || "").trim() || "Your company";
  const listingLabel = ws?.listing_type === "sme" ? "BSE SME / NSE Emerge"
    : ws?.listing_type === "mb" ? "NSE / BSE Main Board" : "Board TBD";

  const handleGo = (go) => {
    if (!go) return;
    if (go.type === "screen") setScreen(go.to);
    else if (go.type === "route") navigate(go.to);
    else if (go.type === "toast") toast.info(go.msg);
  };

  const toggle = (n) => setOpen((o) => ({ ...o, [n]: !o[n] }));

  return (
    <div className="jp" data-testid="journey-planner">
      {/* Target Filing Date control */}
      <div className="jp-controls" data-testid="jp-controls">
        <div className="jpc-ic"><i className="ti ti-calendar-event" aria-hidden="true" /></div>
        <div>
          <div className="jpc-lbl">Target Filing Date</div>
          <div className="jpc-h">Your DRHP filing horizon — stages auto-rescale</div>
        </div>
        <input
          type="date" className="jp-date-input" value={targetDate}
          min={toInputDate(addMonths(today, 3))}
          onChange={(e) => saveDate(e.target.value)}
          data-testid="jp-target-date"
        />
        <div className="jp-presets">
          {PRESETS.map((p) => {
            const pv = toInputDate(addMonths(today, p.m));
            const on = Math.abs(totalMonths - p.m) < 0.6;
            return (
              <button key={p.m} className={`jp-preset${on ? " on" : ""}`} onClick={() => saveDate(pv)} data-testid={`jp-preset-${p.m}`}>{p.label}</button>
            );
          })}
        </div>
        <div className="jp-runway" data-testid="jp-runway">
          <b>{fmtDur(totalMonths)}</b><br />total runway
        </div>
      </div>

      {/* Header bar */}
      <div className="jp-bar">
        <div className="jp-logo"><i className="ti ti-route" aria-hidden="true" /></div>
        <div>
          <div className="jp-title">Journey Planner</div>
          <div className="jp-sub-t">Your step-by-step path to listing</div>
        </div>
        <div className="jp-co" data-testid="jp-company">
          {companyName} · <b>{listingLabel}</b> · ~{Math.max(1, Math.round(filingMonths))} months to file
        </div>
      </div>

      {/* Progress meta */}
      <div className="jp-meta">
        <div className="jp-meta-t">
          <div className="jp-meta-h" data-testid="jp-current-stage">You&apos;re in Stage {stage} — {currentName}</div>
          <div className="jp-meta-d">Click any stage to see its key steps, who owns them, and where to do them. Tap a step to jump straight to that module.</div>
        </div>
        <div className="jp-prog">
          <div className="jp-prog-bar"><div className="jp-prog-f" style={{ width: `${progressPct}%` }} /></div>
          <div className="jp-prog-v" data-testid="jp-progress">{progressPct}%</div>
        </div>
      </div>

      {/* Stages */}
      <div className="stages">
        {plan.map((s) => {
          const state = s.n < stage ? "done" : s.n === stage ? "now" : "next";
          const isOpen = !!open[s.n];
          return (
            <div className={`stage${isOpen ? " open" : ""}`} key={s.n} data-testid={`jp-stage-${s.n}`}>
              <div className="st-head" onClick={() => toggle(s.n)} data-testid={`jp-stage-head-${s.n}`}>
                <div className={`st-num sn-${state}`}>
                  {state === "done" ? <i className="ti ti-check" aria-hidden="true" /> : s.n}
                </div>
                <div className="st-info">
                  <div className="st-name">
                    {s.name}
                    <span className={`st-tl tl-${state}`}>{state === "done" ? "Complete" : state === "now" ? "In progress" : "Upcoming"}</span>
                  </div>
                  <div className="st-sub">{s.sub}</div>
                  <div className="st-window">Planned: <b>{fmtMon(s.start)} → {fmtMon(s.end)}</b></div>
                </div>
                <div className="st-right">
                  <div className="st-time">{s.durLabel}</div>
                  <i className="ti ti-chevron-down chev" aria-hidden="true" />
                </div>
              </div>
              <div className="st-body">
                {s.steps.map((step, i) => {
                  const ckClass = state === "done" ? "done" : state === "now" ? "now" : "";
                  return (
                    <div className="step" key={i} data-testid={`jp-step-${s.n}-${i}`}>
                      <div className={`step-ck ${ckClass}`}>
                        {state === "done" ? <i className="ti ti-check" aria-hidden="true" /> : state === "now" ? <i className="ti ti-dots" aria-hidden="true" /> : null}
                      </div>
                      <div className="step-mid">
                        <div className="step-h">{step.h}</div>
                        <div className="step-d">{step.d}</div>
                        {step.tags.length > 0 && (
                          <div className="step-tags">
                            {step.tags.map((t, j) => (
                              <span className={`s-tag s-${t.k}`} key={j}><i className={`ti ${t.icon}`} aria-hidden="true" /> {t.label}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {step.go && (
                        <button className="step-go" onClick={() => handleGo(step.go)} data-testid={`jp-go-${s.n}-${i}`}>Go →</button>
                      )}
                    </div>
                  );
                })}
                {s.n === STAGES.length && (
                  <div className="note"><i className="ti ti-info-circle" aria-hidden="true" /> Timelines are indicative for an SME IPO and rescale with your Target Filing Date; they vary with disclosure quality, exchange load and market conditions. Confirm specifics with your merchant banker.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JourneyPlanner;
